'use client';

import { usePermission } from '@/hooks/usePermission';
import type OpenReplayTracker from '@openreplay/tracker';
import { useEffect, useRef, useState } from 'react';

const PROJECT_KEY = process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY;
const INGEST_POINT = process.env.NEXT_PUBLIC_OPENREPLAY_INGEST_POINT;

/**
 * Boots OpenReplay session replay together with the Assist plugin, which lets
 * support agents watch a user's live screen and start a WebRTC call from the
 * OpenReplay dashboard.
 *
 * The tracker is loaded dynamically inside an effect so its browser-only code
 * never runs during SSR. When NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY is unset the
 * tracker stays off, so environments without a key are unaffected.
 */
export function OpenReplayProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { userDetails, isLoading } = usePermission();
    const trackerRef = useRef<OpenReplayTracker | null>(null);
    const [trackerStarted, setTrackerStarted] = useState(false);

    useEffect(() => {
        if (!PROJECT_KEY || trackerRef.current) return;

        let cancelled = false;

        const boot = async () => {
            const [{ default: Tracker }, { default: trackerAssist }] =
                await Promise.all([
                    import('@openreplay/tracker'),
                    import('@openreplay/tracker-assist'),
                ]);
            if (cancelled) return;

            // PlainFetched (InlineCssMode 3) inlines each stylesheet as a
            // <style> node so stored sessions keep their CSS; without it,
            // non-localhost builds leave recordings unstyled. `inlineCss` is a
            // real runtime option the package's exported types omit.
            const trackerOptions: ConstructorParameters<typeof Tracker>[0] & {
                inlineCss: number;
            } = {
                projectKey: PROJECT_KEY,
                inlineCss: 3,
                ...(INGEST_POINT ? { ingestPoint: INGEST_POINT } : {}),
            };
            const tracker = new Tracker(trackerOptions);
            tracker.use(trackerAssist());
            trackerRef.current = tracker;

            await tracker.start();
            if (!cancelled) setTrackerStarted(true);
        };

        boot().catch((error) => {
            console.error('[OpenReplay] failed to start tracker', error);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    // Attach the signed-in user so agents can locate their session in Assist.
    useEffect(() => {
        const tracker = trackerRef.current;
        if (!trackerStarted || !tracker || isLoading || !userDetails) return;

        tracker.setUserID(userDetails.email);
        tracker.setMetadata('user_id', userDetails.id);
        if (userDetails.role) {
            tracker.setMetadata('role', userDetails.role);
        }
    }, [trackerStarted, userDetails, isLoading]);

    return <>{children}</>;
}
